import { useEffect, useState } from "react";
import { api, formatCOP, formatNumber } from "@/lib/api";
import { Layers, Package, TrendingDown, Boxes, Info } from "lucide-react";
import LoteDetalleDialog from "@/components/LoteDetalleDialog";
import { toast } from "sonner";
import { useHasPerm } from "@/context/AuthContext";
import DesglosarEmpaqueDialog from "@/components/DesglosarEmpaqueDialog";
import TrasladarLoteDialog from "@/components/TrasladarLoteDialog";

/**
 * Feb/2026: sección "Lotes" dentro del modal de edición de producto.
 * Muestra cada tanda recibida con su cantidad, costo unitario, contenedor y fecha.
 * FIFO: la primera fila de la lista es la que se despacha primero.
 */
export default function LotesInventarioSection({ productoId, onDesglosado }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandAgotados, setExpandAgotados] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const [desglosarOpen, setDesglosarOpen] = useState(false);
  const [trasladar, setTrasladar] = useState(null);
  const [detalleId, setDetalleId] = useState(null);
  const puedeCorregir = useHasPerm("inventario.ajustar");

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const { data: d } = await api.get(`/productos/${productoId}/lotes`);
        if (!cancelled) { setData(d); if (!(d.activos || []).length && (d.agotados || []).length) setExpandAgotados(true); }
      } catch (e) {
        console.error("No se pudo cargar lotes", e);
      } finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [productoId, reloadKey]);

  // Jun/2026: corregir la cantidad recibida de un lote (ej. Excel decía 205, llegaron 500)
  const corregirLote = async (lote) => {
    const val = window.prompt(
      `Cantidad REALMENTE recibida en el lote ${lote.contenedor_id || ""} (actual: ${lote.cantidad_recibida}).\nEl stock del producto se ajustará por la diferencia.`,
      String(lote.cantidad_recibida || 0),
    );
    if (val === null) return;
    const nueva = parseInt(String(val).replace(/[^0-9]/g, ""), 10);
    if (!Number.isFinite(nueva) || nueva === Number(lote.cantidad_recibida)) return;
    const motivo = window.prompt("Motivo de la corrección (ej. packing list: 5 cajas × 100 = 500):", "");
    if (!motivo || !motivo.trim()) { toast.error("Escribe el motivo"); return; }
    try {
      const { data: r } = await api.post(`/productos/lotes/${lote.id}/corregir-cantidad`, { cantidad_recibida: nueva, motivo: motivo.trim() });
      toast.success(`Lote corregido (${r.delta > 0 ? "+" : ""}${r.delta} und). Stock del producto: ${formatNumber(r.stock_actual)}`);
      setReloadKey((k) => k + 1);
      window.dispatchEvent(new CustomEvent("beem-stock-cambiado", { detail: { productoId } }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo corregir el lote");
    }
  };

  if (loading) {
    return (
      <div className="col-span-2 border border-border rounded-lg p-3 bg-muted/10">
        <div className="text-xs text-muted-foreground">Cargando lotes...</div>
      </div>
    );
  }
  if (!data) return null;

  const activos = data.activos || [];
  const agotados = data.agotados || [];

  return (
    <div className="col-span-2 border border-border rounded-lg p-3 bg-muted/10" data-testid="lotes-section">
      <div className="flex flex-wrap items-center justify-between gap-1 mb-2">
        <div className="text-xs font-bold uppercase tracking-[0.14em] text-muted-foreground flex items-center gap-2">
          <Layers className="w-3 h-3" /> Lotes de inventario · FIFO
        </div>
        {puedeCorregir && (activos.length > 0 || agotados.length > 0) && (
          <button type="button" onClick={() => setDesglosarOpen(true)} className="text-[11px] font-semibold text-primary hover:underline flex items-center gap-1" title="El proveedor factura por paquete maestro pero vendes el sub-paquete: multiplica cantidades y divide el costo de todos los lotes" data-testid="lotes-desglosar-btn">
            <Boxes className="w-3 h-3" /> Desglosar empaque ×N
          </button>
        )}
        <DesglosarEmpaqueDialog
          open={desglosarOpen}
          onClose={() => setDesglosarOpen(false)}
          productoId={productoId}
          productoNombre={data.producto_nombre}
          onDone={() => { setReloadKey((k) => k + 1); onDesglosado?.(); window.dispatchEvent(new CustomEvent("beem-stock-cambiado", { detail: { productoId } })); }}
        />
        <LoteDetalleDialog
          loteId={detalleId}
          productoId={productoId}
          productoNombre={data.producto_nombre}
          onClose={() => setDetalleId(null)}
          onCorregir={(l) => corregirLote(l)}
          onCambio={() => { setReloadKey((k) => k + 1); onDesglosado?.(); window.dispatchEvent(new CustomEvent("beem-stock-cambiado", { detail: { productoId } })); }}
        />
        <TrasladarLoteDialog
          open={!!trasladar}
          lote={trasladar}
          onClose={() => setTrasladar(null)}
          productoId={productoId}
          productoNombre={data.producto_nombre}
          onDone={(r) => { setReloadKey((k) => k + 1); onDesglosado?.(); window.dispatchEvent(new CustomEvent("beem-stock-cambiado", { detail: { productoId } })); window.dispatchEvent(new CustomEvent("beem-stock-cambiado", { detail: { productoId: r?.destino?.id } })); }}
        />
        <div className="text-[11px] text-muted-foreground tabular-nums">
          {activos.length} activo{activos.length === 1 ? "" : "s"} · Recibido {formatNumber(data.total_recibido || 0)} · Vendido {formatNumber(data.total_despachado || 0)} · Reservado {formatNumber(data.total_reservado || 0)} · <span className="text-emerald-700 dark:text-emerald-300 font-semibold" data-testid="lotes-disponible-total">Disponible {formatNumber(data.disponible_venta || 0)}</span>
          {data.valor_disponible !== null && data.valor_disponible !== undefined && (
            <span> · {formatCOP(data.valor_disponible)}</span>
          )}
        </div>
      </div>

      {data.costeo && (
        <div className="grid grid-cols-3 gap-2 mb-2" data-testid="costeo-resumen">
          <div className="rounded-md border border-emerald-300/60 dark:border-emerald-800 bg-emerald-50/60 dark:bg-emerald-900/20 px-2 py-1.5">
            <div className="text-[9px] uppercase tracking-wide text-emerald-700 dark:text-emerald-300 font-bold">Costo FIFO actual</div>
            <div className="text-sm font-bold tabular-nums text-emerald-800 dark:text-emerald-200" data-testid="costeo-fifo-actual">{formatCOP(data.costeo.costo_fifo_actual)}</div>
            <div className="text-[9px] text-muted-foreground">Lote que sale ahora · usado en Rentabilidad</div>
          </div>
          <div className="rounded-md border border-border bg-card px-2 py-1.5">
            <div className="text-[9px] uppercase tracking-wide text-muted-foreground font-bold">Promedio inventario</div>
            <div className="text-sm font-bold tabular-nums" data-testid="costeo-promedio">{formatCOP(data.costeo.costo_promedio_disponible)}</div>
            <div className="text-[9px] text-muted-foreground">Ponderado de lotes con saldo</div>
          </div>
          <div className="rounded-md border border-border bg-card px-2 py-1.5">
            <div className="text-[9px] uppercase tracking-wide text-muted-foreground font-bold">Costo ficha</div>
            <div className="text-sm font-bold tabular-nums" data-testid="costeo-ficha">{formatCOP(data.costeo.costo_ficha)}</div>
            <div className="text-[9px] text-muted-foreground">Referencia manual (no afecta utilidad)</div>
          </div>
        </div>
      )}

      {activos.length === 0 ? (
        <div className="text-xs text-muted-foreground py-3 text-center" data-testid="lotes-sin-activos">
          {agotados.length > 0 && Number(data.disponible_venta ?? 0) > 0 ? (
            <>
              El lote de este producto figura <b>agotado</b> pero la ficha aún tiene stock: casi seguro el contenedor se cargó <b>por paquete</b> y cada unidad vendida descontó un paquete entero.
              Usa <b>"Desglosar empaque ×N"</b> (arriba) para convertirlo a unidades: el lote vuelve a tener saldo y el costo queda por unidad.
            </>
          ) : "Sin lotes activos — cuando llegue un contenedor con este producto, aparecerá aquí con su costo real."}
        </div>
      ) : (
        <div className="space-y-1.5">
          {activos.map((lote, idx) => {
            const esInicial = lote.contenedor_id === "INICIAL";
            const consumido = (lote.cantidad_recibida || 0) - (lote.cantidad_disponible || 0);
            const fecha = lote.fecha_recepcion ? new Date(lote.fecha_recepcion).toLocaleDateString("es-CO") : "-";
            return (
              <div
                key={lote.id}
                className="flex items-center gap-3 p-2 rounded-md border border-border bg-card hover:border-primary/30 transition-colors"
                data-testid={`lote-${lote.id}`}
              >
                <div className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/10 text-primary text-[10px] font-bold flex items-center justify-center tabular-nums">
                  {idx + 1}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <div className="text-xs font-semibold text-foreground">
                      {esInicial ? "Lote inicial (migración)" : `Contenedor ${lote.contenedor_id}`}
                    </div>
                    {idx === 0 && (
                      <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-emerald-100 dark:bg-emerald-900/40 text-emerald-800 dark:text-emerald-200 font-bold uppercase tracking-wide">
                        Siguiente en despachar
                      </span>
                    )}
                  </div>
                  <div className="text-[10px] text-muted-foreground tabular-nums flex flex-wrap gap-x-2 gap-y-0.5 mt-0.5">
                    <span>{fecha}</span>
                    <span data-testid={`lote-recibido-${lote.id}`}>Recibido: <b>{formatNumber(lote.cantidad_recibida)}</b></span>
                    <span className="text-rose-600 dark:text-rose-400" data-testid={`lote-despachado-${lote.id}`}>Vendido/despachado: <b>{formatNumber(lote.cantidad_despachada ?? consumido)}</b></span>
                    {lote.cantidad_reservada > 0 && (
                      <span className="text-amber-600 dark:text-amber-400" data-testid={`lote-reservado-${lote.id}`}>Reservado: <b>{formatNumber(lote.cantidad_reservada)}</b></span>
                    )}
                  </div>
                </div>
                <div className="text-right flex-shrink-0">
                  <div className="text-sm font-bold tabular-nums px-2 py-0.5 rounded-md bg-emerald-100 dark:bg-emerald-900/40 text-emerald-800 dark:text-emerald-200" data-testid={`lote-disponible-${lote.id}`}>
                    {formatNumber(lote.cantidad_libre ?? lote.cantidad_disponible)} und
                  </div>
                  <div className="text-[9px] uppercase tracking-wide text-emerald-700 dark:text-emerald-300 font-semibold">Disponible</div>
                  {lote.costo_unitario !== undefined && (
                    <div className="text-[10px] text-muted-foreground tabular-nums" title="Costo unitario de este lote (no es precio de venta)">
                      costo × {formatCOP(lote.costo_unitario)}
                    </div>
                  )}
                </div>
                <button type="button" onClick={() => setDetalleId(lote.id)} className="flex-shrink-0 w-8 h-8 rounded-full border border-border bg-card hover:bg-primary/10 hover:border-primary/40 text-primary flex items-center justify-center transition-colors" title="Ver ficha del lote: contenedor, fila del Excel, cambios y acciones (corregir, trasladar, desglosar)" data-testid={`lote-info-${lote.id}`}>
                  <Info className="w-4 h-4" />
                </button>
              </div>
            );
          })}
        </div>
      )}

      {agotados.length > 0 && (
        <div className="mt-3 pt-3 border-t border-border">
          <button
            type="button"
            onClick={() => setExpandAgotados(!expandAgotados)}
            className="text-[11px] text-muted-foreground hover:text-foreground flex items-center gap-1"
            data-testid="lotes-agotados-toggle"
          >
            <TrendingDown className="w-3 h-3" />
            {agotados.length} lote{agotados.length === 1 ? "" : "s"} agotado{agotados.length === 1 ? "" : "s"}
            {expandAgotados ? " ▼" : " ▶"}
          </button>
          {expandAgotados && (
            <div className="space-y-1 mt-2">
              {agotados.map((lote) => {
                const esInicial = lote.contenedor_id === "INICIAL";
                const fecha = lote.fecha_recepcion ? new Date(lote.fecha_recepcion).toLocaleDateString("es-CO") : "-";
                const agotadoFecha = lote.agotado_at ? new Date(lote.agotado_at).toLocaleDateString("es-CO") : "-";
                return (
                  <div key={lote.id} className="flex items-center gap-2 p-1.5 rounded text-[10px] text-muted-foreground bg-muted/30" data-testid={`lote-agotado-${lote.id}`}>
                    <Package className="w-3 h-3 flex-shrink-0" />
                    <span className="flex-1 truncate">
                      {esInicial ? "Inicial" : lote.contenedor_id} · {fecha} → agotado {agotadoFecha}
                    </span>
                    <span className="tabular-nums">{formatNumber(lote.cantidad_recibida)} und</span>
                    {lote.costo_unitario !== undefined && (
                      <span className="tabular-nums">× {formatCOP(lote.costo_unitario)}</span>
                    )}
                    <button type="button" onClick={() => setDetalleId(lote.id)} className="p-0.5 rounded hover:bg-muted text-primary" title="Ver ficha del lote" data-testid={`lote-info-${lote.id}`}><Info className="w-3.5 h-3.5" /></button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
